import CssBaseline from '@material-ui/core/CssBaseline';
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import platform from 'platform';
import { throttle } from 'lodash';
import ReactGA from 'react-ga';
import { ThemeProvider } from 'styled-components';
import { components } from 'app-webtech-core';
import classNames from 'classnames';
import { WamDialogManager } from 'app-modules/wamDialogs/containers/WamDialogManager';
import { Switcher } from 'app-modules/switcher/components/Switcher';
import { UiEventHandlers } from 'app-modules/ui/containers/UiEventHandlers';
import { Player } from 'app-modules/player/containers/Player';
import { FooterMenu } from 'app-modules/application/containers/FooterMenu';
import { MobileOnly } from 'app-modules/application/components/MediaTargets';
import { Loading } from 'app-modules/application/containers/Loading';
import bootstrapURL from 'app-modules/application/utils/bootstrapURLFromStorage';
import { Trivia } from 'app-modules/trivia/containers';
import { RemoteController } from 'app-modules/remoteController/containers/RemoteController';
import { MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import { withNamespaces } from 'react-i18next';
import { createMuiTheme } from '../styles/createMuiTheme';
import { theme as applicationTheme } from '../../theme';
import { DownloadAppNotification } from '../containers/DownloadAppNotification';
import { EmbedNotification } from '../containers/EmbedNotification';
import { Header } from '../containers/Header';
import { EmbedHeader } from '../containers/EmbedHeader';
import { Meta } from '../containers/Meta';
import { PageTracker } from '../containers/PageTracker';
import { PrivacyPolicyUpdateNotification } from '../containers/PrivacyPolicyUpdate/Notification';
import {
  DESKTOP_GUIDE_WIDTH,
  HEADER_HEIGHT,
  CHAT_WIDTH,
  CHAT_TAB_HEIGHT,
  FOOTER_HEIGHT_MOBILE,
} from '../../base/constants';
import { Menu } from '../containers/Menu';
import RegionBasedGTMTrigger from './GoogleTagManager';

const { Device, PlaybackSession, PlutoErrorBoundary } = components;

const muiTheme = createMuiTheme('#fff');

const styles = (theme) => ({
  applicationWrapper: {
    background: '#000000',
    position: 'fixed',
    width: '100%',
    height: '100%',
    transition: 'width 0.3s',
    [theme.breakpoints.up('md')]: {
      '&.withChat': {
        width: DESKTOP_GUIDE_WIDTH,
      },
      '&.withHeader': {
        height: `calc(100% - ${HEADER_HEIGHT}px)`,
        '&.isFullscreenSplashPage': {
          height: '100%',
        },
      },
    },
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      '&.withHeader': {
        height: `calc(100% - ${FOOTER_HEIGHT_MOBILE}px - ${HEADER_HEIGHT}px)`,
        '&.isFullscreenSplashPage': {
          height: '100%',
        },
      },
    },
    // on mobile landscape, expand player to full screen
    [`${theme.breakpoints.down('sm')} and (orientation: landscape)`]: {
      '&.withHeader': {
        '&.isMobile': {
          height: '100%',
        },
        '&.isFullscreenSplashPage': {
          height: '100%',
        },
      },
    },
  },
  chatWrapper: {
    background: 'rgba(0, 0, 0, 0.8)',
    bottom: 0,
    position: 'absolute',
    '&.withHeader': {
      height: `calc(100vh - 56.25vw - ${HEADER_HEIGHT}px - ${CHAT_TAB_HEIGHT}px) `, // in sync with Watch/Layout.jsx
    },
    width: '100%',
    zIndex: 15,

    [theme.breakpoints.up('md')]: {
      '&.withChat': {
        width: CHAT_WIDTH,
      },
      bottom: 0,
      height: `calc(100vh - ${HEADER_HEIGHT}px)`,
      right: 0,
    },
  },
  embeded: {
    marginTop: 54,
  },
});

class ApplicationComponent extends PureComponent {
  static propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    isChatVisible: PropTypes.bool.isRequired,
    isPlayerEnabled: PropTypes.bool.isRequired,
    headerVisible: PropTypes.bool.isRequired,
    trendingVisible: PropTypes.bool.isRequired,
    isConfigured: PropTypes.bool.isRequired,
    isPrivacyNotificationHidden: PropTypes.bool.isRequired,
    guideVisible: PropTypes.bool.isRequired,
    deviceType: PropTypes.string.isRequired,
    TOSNotificationVisible: PropTypes.bool.isRequired,
    saveUserConsent: PropTypes.func.isRequired,
    userCountryArea: PropTypes.string,
    consentSettings: PropTypes.object,
  };

  state = {
    initialized: false,
    isFullscreenSplashPage: this.props.launchPlayerAsFullscreenSplashPage,
    pageIsReady: false,
  };

  onResize = throttle(() => {
    document.querySelector('#root').style.height = `${window.innerHeight}px`;
  }, 500);

  keyDownHandle = (e) => {
    if (!this.props.isPrivacyNotificationHidden) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  onConsentChange = () => {
    if (global.cbsoptanon) {
      global.cbsoptanon.onScriptsReady((cmp) => {
        cmp.ot.awaitInitialConsent(() => {
          global.cbsoptanon.cmd.push((cmp) => {
            cmp.ot.getState((targeting, performance, functional, social) => {
              this.props.saveUserConsent({ targeting, performance, functional, social });
              this.setState({ initialized: true });
            });
          });
        });
      });
      global.cbsoptanon.cmd.push((cmp) => {
        cmp.ot.addOnConsentChangedHandler(() => {
          cmp.ot.getState((targeting, performance, functional, social) => {
            if (this.state.initialized && performance !== this.props.consentSettings.performance) {
              this.props.saveUserConsent({ targeting, performance, functional, social });
              if (!performance) {
                const id = process.env.GA_TRACKING_ID[this.props.userCountryArea];
                window[`ga-disable-${id}`] = true;
              }
            }
          });
        });
      });
    }
  };

  componentDidMount() {
    this.onConsentChange();
    this.onResize();
    // added 1sec timeout to fix wrong height of root element in landscape view on iPhone X, XR [W50-789]
    // TODO: remove this timeout and impliment portals or mUI modals functionality for popups and overlays components in right positions
    setTimeout(() => this.onResize(), 1000);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.keyDownHandle);
    window.addEventListener('setBootstrapServiceURL', bootstrapURL().setBootstrapURLHandler);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.userCountryArea && this.props.userCountryArea === 'OTHER') {
      ReactGA.initialize(process.env.GA_TRACKING_ID[this.props.userCountryArea], {
        debug: process.env.GA_TRACKING_DEBUG,
        titleCase: false,
      });
    }
    if (
      this.props.userCountryArea &&
      !prevProps.consentSettings.performance &&
      this.props.consentSettings.performance
    ) {
      ReactGA.initialize(process.env.GA_TRACKING_ID[this.props.userCountryArea], {
        debug: process.env.GA_TRACKING_DEBUG,
        titleCase: false,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.keyDownHandle);
    window.removeEventListener('setBootstrapServiceURL', bootstrapURL().setBootstrapURLHandler);
    
  }

  exitFullscreenSplashPage = () => {
    this.setState({
      isFullscreenSplashPage: false,
    });
  };

  setPageIsReady = () => {
    this.setState({
      pageIsReady: true,
    });
  };

  render() {
    const {
      classes,
      isChatVisible,
      isPlayerEnabled,
      headerVisible,
      trendingVisible,
      deviceType,
      guideVisible,
      TOSNotificationVisible,
      isMobile,
      t,
    } = this.props;

    return (
      <PlutoErrorBoundary>
        <ThemeProvider theme={applicationTheme}>
          <MuiThemeProvider theme={muiTheme}>
            <Fragment>
              {TOSNotificationVisible && this.state.pageIsReady && (
                <PrivacyPolicyUpdateNotification />
              )}
              <RegionBasedGTMTrigger />
              <RemoteController />
              <DownloadAppNotification />
              <EmbedNotification />
              <CssBaseline />
              <Loading
                setPageIsReady={this.setPageIsReady}
                text={t('drop_in_watch_free', "Drop In. Watch Free.")}
              />
              <Meta />
              <UiEventHandlers />
              <Device
                type={deviceType}
                make={platform.name}
                version={platform.version}
                userAgent={navigator.userAgent}
                DNT={false}
              />
              <PlaybackSession />
              <WamDialogManager />
              <PageTracker />
              {headerVisible && !this.state.isFullscreenSplashPage && (
                <Header trendingVisible={trendingVisible} />
              )}
              {!headerVisible && <EmbedHeader />}
              <Menu trendingVisible={trendingVisible} />
              <div
                className={classNames(classes.applicationWrapper, {
                  withChat: isChatVisible,
                  withHeader: headerVisible,
                  withGuide: guideVisible,
                  isMobile: isMobile,
                  isFullscreenSplashPage: this.state.isFullscreenSplashPage,
                })}
              >
                {isPlayerEnabled && (
                  <Player
                    guideVisible={guideVisible}
                    isFullscreenSplashPage={this.state.isFullscreenSplashPage}
                    exitFullscreenSplashPage={this.exitFullscreenSplashPage}
                    TOSNotificationVisible={TOSNotificationVisible}
                  />
                )}
                <Switcher headerVisible={headerVisible} guideVisible={guideVisible} />
              </div>
              <Trivia />
              <MobileOnly>
                {headerVisible && !this.state.isFullscreenSplashPage && (
                  <FooterMenu trendingVisible={trendingVisible} />
                )}
              </MobileOnly>
            </Fragment>
          </MuiThemeProvider>
        </ThemeProvider>
      </PlutoErrorBoundary>
    );
  }
}

export const ApplicationRootComponent = withStyles(styles)(withNamespaces()(ApplicationComponent));
